import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { 
  Smartphone, Heart, MapPin, Bell, Users, Shield,
  Activity, Moon, Footprints, AlertTriangle
} from 'lucide-react';

const steps = [
  { number: '01', title: 'Wear the Band', description: 'Slip on Connect like any comfortable bracelet. The soft, breathable fabric is designed for 24/7 wear across all ages—from active kids to aging parents.', icon: Activity },
  { number: '02', title: 'Download the Connect App', description: 'Available on iOS and Android. Create your family circle in minutes. Each family member gets their own profile with customizable settings.', icon: Smartphone },
  { number: '03', title: 'Pair Your Devices', description: "Simple Bluetooth pairing. No complicated setup. Just scan, connect, and you're ready to go. Each band syncs automatically when in range.", icon: Shield },
  { number: '04', title: 'Monitor Together', description: 'See health metrics, location updates, and safety alerts—all in one unified family dashboard. Peace of mind, delivered seamlessly.', icon: Users },
];

const features = [
  { icon: Heart, title: 'Heart Rate Monitoring', description: 'Continuous tracking with intelligent alerts for unusual patterns.' },
  { icon: Moon, title: 'Sleep Analysis', description: 'Track sleep quality and patterns to improve rest for the whole family.' },
  { icon: Footprints, title: 'Activity Tracking', description: 'Steps, active minutes, and movement goals tailored to each person.' },
  { icon: MapPin, title: 'Real-Time GPS', description: 'Know where family members are without intrusive check-ins.' },
  { icon: Bell, title: 'Smart Geofencing', description: 'Get notified when loved ones arrive or leave designated areas.' },
  { icon: AlertTriangle, title: 'Emergency SOS', description: 'One-touch alert that shares location with all family members instantly.' },
];

const HowItWorksPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" data-testid="how-it-works-page">
      <Navigation />

      {/* Hero */}
      <section className="page-header" data-testid="how-hero">
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#1A1A1A] mb-6">
              Simple by Design. Powerful by Nature.
            </h1>
            <p className="text-[#5A5A5A] text-lg md:text-xl leading-relaxed">
              Connect removes complexity so you can focus on what matters. 
              Here's how it works, step by step.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps */}
      <section className="section" data-testid="how-steps">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex gap-8 md:gap-12 mb-16 last:mb-0"
                data-testid={`how-step-${step.number}`}
              >
                <div className="flex flex-col items-center">
                  <span className="text-4xl md:text-5xl font-semibold text-black/8">{step.number}</span>
                  {index < steps.length - 1 && (
                    <div className="w-px h-full bg-gradient-to-b from-black/10 to-transparent mt-4" />
                  )}
                </div>

                <div className="flex-1 pb-8">
                  <div className="w-12 h-12 rounded-xl bg-white border border-black/8 shadow-sm flex items-center justify-center mb-4">
                    <step.icon className="w-5 h-5 text-[#4A4A4A]" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-3">{step.title}</h3>
                  <p className="text-[#5A5A5A] text-lg leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section section-soft" data-testid="how-features">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1A1A1A] mb-4">
              What You Can Monitor
            </h2>
            <p className="text-[#5A5A5A] text-lg">
              All the insights that matter, none of the noise.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="wellness-card"
                data-testid={`how-feature-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-black/5 flex items-center justify-center mb-4">
                  <feature.icon className="w-5 h-5 text-[#4A4A4A]" />
                </div>
                <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">{feature.title}</h3>
                <p className="text-[#5A5A5A] text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section" data-testid="how-cta">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1A1A1A] mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-[#5A5A5A] text-lg mb-8">
              Join the waitlist and be among the first to experience Connect.
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/reserve')}
              className="bg-[#1A1A1A] text-white px-8 py-4 rounded-full text-lg font-medium
                         hover:bg-[#333] transition-all duration-300"
              data-testid="how-reserve-btn"
            >
              Reserve Your Band
            </motion.button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorksPage;
